import { useEffect, useMemo, useState } from 'react';
import {
  AppShell,
  Button,
  Card,
  Dialog,
  EmptyState,
  Icon,
  Input,
  MoneyInput,
  ProgressBar,
  SegmentedControl,
  Sheet,
  Skeleton
} from '../../../ui';
import { useAtualizacaoContexto } from '../../../contextos/AtualizacaoContexto';
import { usePerfilContexto } from '../../../contextos/PerfilContexto';
import { Categoria as TipoCategoria } from '../../../servicos/categorias/typeCategoria';
import { useCategorias } from '../../../servicos/categorias/useCategorias';
import axios from 'axios';
import { Transacao } from '../../../servicos/transacoes/typeTransacao';
import { useTransacoes } from '../../../servicos/transacoes/useTransacoes';
import useAvisos from '../../../hooks/useAviso';
import { cores, icones } from '../../../utils/Listas';
import { extrairMesAno, mensagemDeErro, primeiroDiaMesAtual, ultimoDiaMesAtual, valorParaTexto } from '../../../utils/Utils';
import { dataDaTransacao, realizadoDoMesPorCategoria } from './realizado';
import styles from './Categoria.module.css';

const OPCOES_TIPO = [
  { id: 'D', rotulo: 'Despesas', icone: 'trending_down' },
  { id: 'R', rotulo: 'Receitas', icone: 'trending_up' }
];

/**
 * Tela de Categorias — grid compacto por tipo (despesas/receitas) com o
 * realizado do mês em cada categoria e progresso contra a meta mensal.
 * Formulário em Sheet com pickers de cor e de ícone (com busca).
 */
export default function Categoria() {
  const { categorias, loading, createCategoria, updateCategoria, deleteCategoria, fetchCategorias } = useCategorias();
  const { perfil } = usePerfilContexto();
  const { adicionarAviso } = useAvisos();
  const [restaurando, setRestaurando] = useState(false);

  // Zona de perigo apagou tudo? Recria as categorias padrão (só as que faltam).
  const restaurarPadrao = async () => {
    if (restaurando) return;
    setRestaurando(true);
    try {
      const token = localStorage.getItem('token');
      const resposta = await axios.post<number>(
        process.env.REACT_APP_API_URL + '/categorias/padrao',
        null,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      adicionarAviso(`${resposta.data} categoria(s) padrão restaurada(s).`, 'sucesso');
      await fetchCategorias();
    } catch (err) {
      adicionarAviso(mensagemDeErro(err) ?? 'Não foi possível restaurar as categorias padrão.');
    } finally {
      setRestaurando(false);
    }
  };
  // Primeira busca ainda no ar: nunca mostrar "nenhuma categoria" antes de saber.
  const carregandoLista = categorias.length === 0 && (loading || !!perfil.carregarCategorias);
  const { atualizar } = useAtualizacaoContexto();
  const { fetchTransacoes } = useTransacoes(false);

  const [transacoesPeriodo, setTransacoesPeriodo] = useState<Transacao[]>([]);
  const [tipoAba, setTipoAba] = useState<string>('D');

  const [formAberto, setFormAberto] = useState(false);
  const [editando, setEditando] = useState<TipoCategoria | null>(null);
  const [excluindo, setExcluindo] = useState<TipoCategoria | null>(null);
  const [menuAberto, setMenuAberto] = useState<number | null>(null);

  const [tipoForm, setTipoForm] = useState<string>('D');
  const [nome, setNome] = useState('');
  const [meta, setMeta] = useState(0);
  const [cor, setCor] = useState('');
  const [icone, setIcone] = useState('');
  const [buscaIcone, setBuscaIcone] = useState('');
  const [erros, setErros] = useState<Record<string, string>>({});

  // Busca os últimos 3 meses (até o fim do mês atual): o mês corrente alimenta
  // o realizado dos cards e a janela toda vira a média mensal sugerida no form.
  useEffect(() => {
    let ativo = true;
    const hoje = new Date();
    const buscar = async () => {
      const colecao = await fetchTransacoes({
        dataInicial: new Date(hoje.getFullYear(), hoje.getMonth() - 2, 1),
        dataFinal: ultimoDiaMesAtual()
      });
      if (ativo) setTransacoesPeriodo(colecao || []);
    };
    buscar();
    return () => {
      ativo = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [atualizar]);

  // Fecha o menu de ações ao clicar em qualquer lugar fora dele.
  useEffect(() => {
    if (menuAberto === null) return;
    const fechar = () => setMenuAberto(null);
    document.addEventListener('click', fechar);
    return () => document.removeEventListener('click', fechar);
  }, [menuAberto]);

  // Realizado do mês atual por categoria (chave: "categoriaId-tipo").
  const realizadoMes = useMemo(
    () => realizadoDoMesPorCategoria(transacoesPeriodo, primeiroDiaMesAtual()),
    [transacoesPeriodo]
  );

  // Média mensal por categoria na janela buscada (divide pelos meses com dados).
  const mediaMensal = useMemo(() => {
    const totais = new Map<string, number>();
    const meses = new Map<string, Set<string>>();
    transacoesPeriodo.forEach((transacao) => {
      if (!transacao.categoriaId) return;
      const chave = `${transacao.categoriaId}-${transacao.tipo}`;
      totais.set(chave, (totais.get(chave) || 0) + transacao.valor);
      const mesAno = extrairMesAno(dataDaTransacao(transacao.data));
      if (!meses.has(chave)) meses.set(chave, new Set());
      meses.get(chave)?.add(mesAno);
    });
    const mapa = new Map<string, number>();
    totais.forEach((total, chave) => {
      mapa.set(chave, total / Math.max(meses.get(chave)?.size || 1, 1));
    });
    return mapa;
  }, [transacoesPeriodo]);

  const categoriasDaAba = categorias.filter((categoria) => categoria.tipo === tipoAba);

  const abrirCriacao = () => {
    setEditando(null);
    setTipoForm(tipoAba);
    setNome('');
    setMeta(0);
    setCor('');
    setIcone('');
    setBuscaIcone('');
    setErros({});
    setFormAberto(true);
  };

  const abrirEdicao = (categoria: TipoCategoria) => {
    setEditando(categoria);
    setTipoForm(categoria.tipo);
    setNome(categoria.nome);
    setMeta(categoria.valorMeta || 0);
    setCor(categoria.corIdentificacao);
    setIcone(categoria.icone);
    setBuscaIcone('');
    setErros({});
    setFormAberto(true);
  };

  const fecharForm = () => {
    setFormAberto(false);
    setEditando(null);
  };

  const limparErro = (campo: string) => {
    setErros((anteriores) => {
      if (!anteriores[campo]) return anteriores;
      const { [campo]: removido, ...restantes } = anteriores;
      return restantes;
    });
  };

  const handleSalvar = async () => {
    const novosErros: Record<string, string> = {};
    if (!nome.trim()) novosErros.nome = 'Dê um nome à categoria.';
    if (!cor) novosErros.cor = 'Escolha uma cor de identificação.';
    if (!icone) novosErros.icone = 'Escolha um ícone.';

    if (Object.keys(novosErros).length > 0) {
      setErros(novosErros);
      return;
    }

    // Mesmo payload do formulário anterior; meta zerada vira undefined.
    const payload = {
      nome: nome.trim(),
      corIdentificacao: cor,
      tipo: tipoForm,
      icone,
      valorMeta: meta === 0 ? undefined : meta
    };

    if (editando && editando.id !== undefined) {
      await updateCategoria(editando.id, payload);
    } else {
      await createCategoria(payload);
    }

    fecharForm();
  };

  const confirmarExclusao = async () => {
    if (excluindo && excluindo.id !== undefined) {
      await deleteCategoria([excluindo.id.toString()]);
    }
    setExcluindo(null);
  };

  const filtroIcone = buscaIcone.trim().toLowerCase().replace(/\s+/g, '_');
  const iconesFiltrados = filtroIcone ? icones.filter((nomeIcone) => nomeIcone.includes(filtroIcone)) : icones;

  const mediaDaCategoriaEmEdicao =
    editando && editando.id !== undefined ? mediaMensal.get(`${editando.id}-${tipoForm}`) || 0 : 0;

  return (
    <AppShell
      titulo="Categorias"
      subtitulo="Organize despesas e receitas por categoria e acompanhe o realizado do mês contra as metas."
      acoes={
        <Button icone="add" onClick={abrirCriacao}>
          Nova categoria
        </Button>
      }
    >
      <div className={styles.abas}>
        <SegmentedControl opcoes={OPCOES_TIPO} valor={tipoAba} onChange={setTipoAba} />
      </div>

      {carregandoLista ? (
        <Skeleton tipo="cards" quantidade={6} altura={140} />
      ) : categoriasDaAba.length === 0 ? (
        <EmptyState
          icone="category"
          titulo={tipoAba === 'D' ? 'Nenhuma categoria de despesa' : 'Nenhuma categoria de receita'}
          descricao={
            tipoAba === 'D'
              ? 'Crie categorias como Mercado, Moradia e Lazer, defina metas mensais e veja exatamente para onde o dinheiro vai.'
              : 'Crie categorias como Salário, Freelas e Investimentos para saber de onde o dinheiro vem todo mês.'
          }
          acao={
            <>
              <Button icone="add" onClick={abrirCriacao}>
                {tipoAba === 'D' ? 'Criar categoria de despesa' : 'Criar categoria de receita'}
              </Button>
              <Button variante="contorno" icone="settings_backup_restore" carregando={restaurando} onClick={restaurarPadrao}>
                Restaurar categorias padrão
              </Button>
            </>
          }
        />
      ) : (
        <div className={styles.grid}>
          {categoriasDaAba.map((categoria) => {
            const realizado = realizadoMes.get(`${categoria.id}-${categoria.tipo}`) || 0;
            const temMeta = !!categoria.valorMeta && categoria.valorMeta > 0;
            const percentual = temMeta ? (realizado / (categoria.valorMeta || 1)) * 100 : 0;

            return (
              <Card key={categoria.id} className={styles.cardCategoria}>
                <div className={styles.topo}>
                  <div className={styles.iconeCategoria} style={{ background: `${categoria.corIdentificacao}1a` }}>
                    <Icon nome={categoria.icone} tamanho={20} cor={categoria.corIdentificacao} />
                  </div>
                  <span className={styles.nome}>{categoria.nome}</span>
                  <div className={styles.menuArea}>
                    <button
                      type="button"
                      className={styles.menuBotao}
                      aria-label="Ações da categoria"
                      onClick={(evento) => {
                        evento.stopPropagation();
                        setMenuAberto(menuAberto === categoria.id ? null : categoria.id ?? null);
                      }}
                    >
                      <Icon nome="more_vert" tamanho={18} />
                    </button>
                    {menuAberto === categoria.id && (
                      <div className={styles.menu}>
                        <button type="button" className={styles.menuItem} onClick={() => abrirEdicao(categoria)}>
                          <Icon nome="edit" tamanho={16} />
                          Editar
                        </button>
                        <button
                          type="button"
                          className={`${styles.menuItem} ${styles.menuItemPerigo}`}
                          onClick={() => setExcluindo(categoria)}
                        >
                          <Icon nome="delete" tamanho={16} />
                          Excluir
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                <div className={styles.rodapeCard}>
                  {/* Mesma estrutura com e sem meta: texto + barra + linha de meta.
                      Cards ficam da mesma altura, sem layout "pulando" entre eles. */}
                  <span className={styles.realizadoTexto}>
                    {categoria.tipo === 'D' ? 'Gasto este mês: ' : 'Recebido este mês: '}
                    {valorParaTexto(realizado, true)}
                  </span>
                  <ProgressBar
                    percentual={temMeta ? percentual : 0}
                    tom={categoria.tipo === 'D' ? 'auto-despesa' : 'auto-receita'}
                  />
                  {temMeta ? (
                    <span className={styles.metaTexto}>
                      Meta: {valorParaTexto(realizado, true)} de {valorParaTexto(categoria.valorMeta, true)}
                    </span>
                  ) : (
                    <button type="button" className={styles.semMeta} onClick={() => abrirEdicao(categoria)}>
                      Definir meta
                    </button>
                  )}
                </div>
              </Card>
            );
          })}
        </div>
      )}

      <Sheet
        aberto={formAberto}
        onFechar={fecharForm}
        titulo={editando ? 'Editar categoria' : 'Nova categoria'}
        subtitulo={
          editando
            ? 'Ajuste nome, meta, cor ou ícone da categoria.'
            : 'Categorias com meta mensal viram limites acompanháveis no dashboard.'
        }
        rodape={
          <>
            <Button variante="contorno" onClick={fecharForm}>
              Cancelar
            </Button>
            <Button icone="check" carregando={loading} onClick={handleSalvar}>
              {editando ? 'Salvar alterações' : 'Adicionar categoria'}
            </Button>
          </>
        }
      >
        <div className={styles.form}>
          <div className={styles.campo}>
            <span className={styles.rotuloCampo}>Tipo</span>
            <SegmentedControl
              opcoes={[
                { id: 'D', rotulo: 'Despesa', icone: 'trending_down' },
                { id: 'R', rotulo: 'Receita', icone: 'trending_up' }
              ]}
              valor={tipoForm}
              onChange={setTipoForm}
            />
          </div>
          <Input
            label="Nome da categoria"
            valor={nome}
            onChange={(valor) => {
              setNome(valor);
              limparErro('nome');
            }}
            placeholder="Ex.: Mercado, Moradia, Salário..."
            icone="edit"
            erro={erros.nome}
            obrigatorio
            autoFocus
          />
          <MoneyInput
            label="Meta mensal (opcional)"
            valor={meta}
            onChange={setMeta}
            dica={
              mediaDaCategoriaEmEdicao > 0
                ? `Média dos últimos meses: ${valorParaTexto(mediaDaCategoriaEmEdicao, true)}`
                : undefined
            }
          />

          <div className={styles.campo}>
            <span className={styles.rotuloCampo}>
              Cor de identificação <span className={styles.asterisco}>*</span>
            </span>
            <div className={styles.gradeCores}>
              {cores.map((opcaoCor) => (
                <button
                  key={opcaoCor}
                  type="button"
                  aria-label={`Cor ${opcaoCor}`}
                  className={`${styles.swatch} ${cor === opcaoCor ? styles.swatchSelecionado : ''}`}
                  style={{ backgroundColor: opcaoCor }}
                  onClick={() => {
                    setCor(opcaoCor);
                    limparErro('cor');
                  }}
                />
              ))}
            </div>
            {erros.cor && <span className={styles.erroCampo}>{erros.cor}</span>}
          </div>

          <div className={styles.campo}>
            <span className={styles.rotuloCampo}>
              Ícone <span className={styles.asterisco}>*</span>
            </span>
            <Input valor={buscaIcone} onChange={setBuscaIcone} placeholder="Buscar ícone pelo nome..." icone="search" />
            <div className={styles.gradeIcones}>
              {iconesFiltrados.map((opcaoIcone) => (
                <button
                  key={opcaoIcone}
                  type="button"
                  title={opcaoIcone}
                  className={`${styles.itemIcone} ${icone === opcaoIcone ? styles.itemIconeSelecionado : ''}`}
                  onClick={() => {
                    setIcone(opcaoIcone);
                    limparErro('icone');
                  }}
                >
                  <Icon nome={opcaoIcone} tamanho={20} />
                </button>
              ))}
              {iconesFiltrados.length === 0 && <span className={styles.buscaVazia}>Nenhum ícone encontrado.</span>}
            </div>
            {erros.icone && <span className={styles.erroCampo}>{erros.icone}</span>}
          </div>
        </div>
      </Sheet>

      <Dialog
        aberto={excluindo !== null}
        onFechar={() => setExcluindo(null)}
        titulo="Excluir categoria"
        descricao={`Excluir "${excluindo?.nome}"? As movimentações associadas serão desvinculadas, mas o histórico permanece.`}
        acoes={
          <>
            <Button variante="contorno" onClick={() => setExcluindo(null)}>
              Cancelar
            </Button>
            <Button variante="perigo" icone="delete" carregando={loading} onClick={confirmarExclusao}>
              Excluir
            </Button>
          </>
        }
      />
    </AppShell>
  );
}
